/* ═══ AgInput — Anagogia Design System ═══ */

import { forwardRef, useId, useState } from 'react';
import styles from './AgInput.module.css';

interface AgInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Visible label above the input */
  label?: string;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Error state — turns border red */
  error?: boolean;
  /** Helper / error text below the input */
  helperText?: string;
  /** Stretch to fill parent width */
  fullWidth?: boolean;
  /** Content placed before the input text (icon, prefix, etc.) */
  startAdornment?: React.ReactNode;
  /** Content placed after the input text (icon, suffix, etc.) */
  endAdornment?: React.ReactNode;
  /** Render a <textarea> instead of <input> */
  multiline?: boolean;
  /** Number of visible text rows (multiline only) */
  rows?: number;
}

/**
 * Champ de saisie texte avec label, texte d'aide et état d'erreur.
 * @category Formulaires
 */
const AgInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, AgInputProps>(
  function AgInput(
    {
      label,
      size = 'md',
      error = false,
      helperText,
      fullWidth = false,
      startAdornment,
      endAdornment,
      multiline = false,
      rows = 3,
      className,
      disabled,
      id: idProp,
      onFocus,
      onBlur,
      ...rest
    },
    ref,
  ) {
    const autoId = useId();
    const inputId = idProp ?? autoId;
    const helperId = helperText ? `${inputId}-helper` : undefined;

    const [focused, setFocused] = useState(false);

    const handleFocus = (
      e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setFocused(true);
      onFocus?.(e as React.FocusEvent<HTMLInputElement>);
    };

    const handleBlur = (
      e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setFocused(false);
      onBlur?.(e as React.FocusEvent<HTMLInputElement>);
    };

    const wrapperClass = [
      styles.wrapper,
      fullWidth && styles.fullWidth,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const containerClass = [
      styles.inputContainer,
      styles[size],
      focused && styles.focused,
      error && styles.error,
      disabled && styles.disabled,
      multiline && styles.textarea,
    ]
      .filter(Boolean)
      .join(' ');

    const sharedProps = {
      id: inputId,
      className: styles.input,
      disabled,
      'aria-invalid': error || undefined,
      'aria-describedby': helperId,
      onFocus: handleFocus,
      onBlur: handleBlur,
    };

    return (
      <div className={wrapperClass}>
        {label && (
          <label className={styles.label} htmlFor={inputId}>
            {label}
          </label>
        )}

        <div className={containerClass}>
          {startAdornment && (
            <span className={styles.adornment}>{startAdornment}</span>
          )}

          {multiline ? (
            <textarea
              ref={ref as React.Ref<HTMLTextAreaElement>}
              rows={rows}
              {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
              {...sharedProps}
            />
          ) : (
            <input
              ref={ref as React.Ref<HTMLInputElement>}
              {...rest}
              {...sharedProps}
            />
          )}

          {endAdornment && (
            <span className={styles.adornment}>{endAdornment}</span>
          )}
        </div>

        {helperText && (
          <span
            id={helperId}
            className={[
              styles.helperText,
              error && styles.helperTextError,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {helperText}
          </span>
        )}
      </div>
    );
  },
);

export { AgInput };
export type { AgInputProps };
export default AgInput;
